import { useState, useEffect } from 'react';
import * as AccountsDB from '@/lib/personalAccountsDb';
import { getCachedExchangeRates, type ExchangeRates } from '@/lib/exchangeRateService';

interface NewMovementModalProps {
  accountId: string;
  onClose: () => void;
  onSuccess: () => void;
}

type MovementTab = 'para_verildi' | 'para_alindi' | 'urun_verildi' | 'altin_verildi' | 'doviz_verildi' | 'urun_iade' | 'duzeltme';

const TABS: { key: MovementTab; label: string; icon: string; color: string }[] = [
  { key: 'para_verildi', label: 'Para Ver', icon: 'ri-money-dollar-circle-line', color: 'amber' },
  { key: 'para_alindi', label: 'Para Al', icon: 'ri-bank-card-line', color: 'emerald' },
  { key: 'urun_verildi', label: 'Ürün Ver', icon: 'ri-archive-line', color: 'sky' },
  { key: 'altin_verildi', label: 'Altın Ver', icon: 'ri-copper-coin-line', color: 'yellow' },
  { key: 'doviz_verildi', label: 'Döviz Ver', icon: 'ri-exchange-dollar-line', color: 'indigo' },
  { key: 'urun_iade', label: 'Ürün İade', icon: 'ri-arrow-go-back-line', color: 'rose' },
  { key: 'duzeltme', label: 'Düzeltme', icon: 'ri-pencil-line', color: 'gray' },
];

function formatNumber(n: number): string {
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function NewMovementModal({ accountId, onClose, onSuccess }: NewMovementModalProps) {
  const [activeTab, setActiveTab] = useState<MovementTab>('para_verildi');
  const [rates, setRates] = useState<ExchangeRates | null>(null);

  // Common fields
  const [amount, setAmount] = useState('');
  const [movementDate, setMovementDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [reason, setReason] = useState('gecici_borc');
  const [description, setDescription] = useState('');

  // Currency fields (Para Ver/Al)
  const [currencyCode, setCurrencyCode] = useState<AccountsDB.CurrencyCode>('TRY');
  const [exchangeRate, setExchangeRate] = useState('');

  // Product fields
  const [productName, setProductName] = useState('');
  const [modelCode, setModelCode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');

  // Gold fields
  const [goldType, setGoldType] = useState<AccountsDB.GoldType>('gram');
  const [goldQuantity, setGoldQuantity] = useState('1');
  const [goldGramEquivalent, setGoldGramEquivalent] = useState('');
  const [goldRate, setGoldRate] = useState('');

  // Currency (Döviz Ver) fields
  const [currencyCodeDvz, setCurrencyCodeDvz] = useState<'USD' | 'EUR' | 'GBP'>('USD');
  const [currencyAmountDvz, setCurrencyAmountDvz] = useState('');
  const [exchangeRateDvz, setExchangeRateDvz] = useState('');

  // Return fields
  const [returnProductName, setReturnProductName] = useState('');
  const [returnAmount, setReturnAmount] = useState('');
  const [returnQuantity, setReturnQuantity] = useState('1');

  // Düzeltme sign
  const [correctionSign, setCorrectionSign] = useState<'positive' | 'negative'>('positive');

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load cached exchange rates
  useEffect(() => {
    const cached = getCachedExchangeRates();
    if (cached) setRates(cached);
  }, []);

  // Auto-fill exchange rate and gold rate when currency/gold changes
  useEffect(() => {
    if (!rates) return;
    if (activeTab === 'para_verildi' || activeTab === 'para_alindi') {
      if (currencyCode === 'USD') setExchangeRate(String(rates.usd_try));
      else if (currencyCode === 'EUR') setExchangeRate(String(rates.eur_try));
      else if (currencyCode === 'GBP' && rates.gbp_try) setExchangeRate(String(rates.gbp_try));
    }
    if (activeTab === 'altin_verildi' && rates.gold_try) {
      setGoldRate(String(rates.gold_try));
    }
    if (activeTab === 'doviz_verildi') {
      if (currencyCodeDvz === 'USD') setExchangeRateDvz(String(rates.usd_try));
      else if (currencyCodeDvz === 'EUR') setExchangeRateDvz(String(rates.eur_try));
      else if (currencyCodeDvz === 'GBP' && rates.gbp_try) setExchangeRateDvz(String(rates.gbp_try));
    }
  }, [activeTab, currencyCode, currencyCodeDvz, goldType, rates]);

  // Compute gold gram equivalent when type or quantity changes
  useEffect(() => {
    const gtInfo = AccountsDB.GOLD_TYPE_LABELS[goldType];
    if (gtInfo && goldType !== 'bilezik') {
      const q = Number(goldQuantity) || 0;
      setGoldGramEquivalent(String(q * gtInfo.gramEquivalent));
    }
  }, [goldType, goldQuantity]);

  const getTlEquivalent = (): number => {
    if (activeTab === 'altin_verildi') {
      const gramEq = Number(goldGramEquivalent) || 0;
      const rate = Number(goldRate) || 0;
      return Math.round(gramEq * rate * 100) / 100;
    }
    if (activeTab === 'doviz_verildi') {
      const amt = Number(currencyAmountDvz) || 0;
      const rate = Number(exchangeRateDvz) || 0;
      return Math.round(amt * rate * 100) / 100;
    }
    if (activeTab === 'para_verildi' || activeTab === 'para_alindi') {
      const amt = Number(amount) || 0;
      if (currencyCode === 'TRY') return amt;
      const rate = Number(exchangeRate) || 0;
      return Math.round(amt * rate * 100) / 100;
    }
    return 0;
  };

  const getPrincipalAmount = (): number => {
    if (activeTab === 'urun_verildi') {
      if (amount) return Number(amount) || 0;
      const q = Number(quantity) || 0;
      const up = Number(unitPrice) || 0;
      return q * up;
    }
    if (activeTab === 'urun_iade') {
      return Number(returnAmount) || 0;
    }
    if (activeTab === 'duzeltme') {
      return Number(amount) || 0;
    }
    return getTlEquivalent();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const principal = getPrincipalAmount();

    if (activeTab !== 'duzeltme' && principal <= 0) {
      setErr('Tutar 0\'dan büyük olmalıdır.');
      return;
    }

    if (activeTab === 'urun_verildi' && !productName.trim()) {
      setErr('Ürün adı zorunludur.');
      return;
    }

    if (activeTab === 'altin_verildi') {
      if (!goldQuantity || Number(goldQuantity) <= 0) {
        setErr('Altın miktarı girilmelidir.');
        return;
      }
      if (goldType === 'bilezik' && !goldGramEquivalent) {
        setErr('Bilezik için gram karşılığı girilmelidir.');
        return;
      }
    }

    if (activeTab === 'doviz_verildi' && (!currencyAmountDvz || Number(currencyAmountDvz) <= 0)) {
      setErr('Döviz tutarı girilmelidir.');
      return;
    }

    if (activeTab === 'urun_iade' && (!returnAmount || Number(returnAmount) <= 0)) {
      setErr('İade tutarı girilmelidir.');
      return;
    }

    const isReceive = activeTab === 'para_verildi' || activeTab === 'urun_verildi' || activeTab === 'altin_verildi' || activeTab === 'doviz_verildi' || (activeTab === 'duzeltme' && correctionSign === 'positive');
    const direction = isReceive ? 'receivable' : 'settlement';
    const finalAmount = activeTab === 'duzeltme' ? Math.abs(principal) : principal;

    // Build payload
    let moveType = activeTab;
    let cc = 'TRY';
    let qty = 1;
    let unt = 'TL';
    let up: number | null = null;
    let er: number | null = null;
    let gr: number | null = null;
    let assetName: string | null = null;
    let prodName: string | null = null;
    let mdlCode: string | null = null;

    if (activeTab === 'para_verildi' || activeTab === 'para_alindi') {
      cc = currencyCode;
      qty = Number(amount) || 0;
      unt = AccountsDB.CURRENCY_LABELS[cc]?.unit || 'TL';
      er = currencyCode !== 'TRY' ? (Number(exchangeRate) || null) : null;
    } else if (activeTab === 'urun_verildi') {
      prodName = productName.trim();
      mdlCode = modelCode.trim() || null;
      qty = Number(quantity) || 1;
      unt = 'adet';
      up = Number(unitPrice) || null;
      cc = 'TRY';
    } else if (activeTab === 'altin_verildi') {
      cc = 'XAU';
      const gtLabel = AccountsDB.GOLD_TYPE_LABELS[goldType].label;
      assetName = gtLabel;
      qty = Number(goldQuantity) || 0;
      unt = 'gram';
      gr = Number(goldRate) || null;
      up = gr;
      er = null;
    } else if (activeTab === 'doviz_verildi') {
      cc = currencyCodeDvz;
      qty = Number(currencyAmountDvz) || 0;
      unt = currencyCodeDvz;
      er = Number(exchangeRateDvz) || null;
      up = er;
    } else if (activeTab === 'urun_iade') {
      prodName = returnProductName.trim() || null;
      qty = Number(returnQuantity) || 1;
      unt = 'adet';
      cc = 'TRY';
    } else if (activeTab === 'duzeltme') {
      cc = 'TRY';
      qty = 1;
      unt = 'TL';
    }

    const tlEq = cc !== 'TRY' ? getTlEquivalent() : finalAmount;

    setSaving(true);
    setErr(null);

    const res = await AccountsDB.addMovement({
      personal_account_id: accountId,
      movement_type: moveType,
      direction,
      principal_amount: cc !== 'TRY' ? tlEq : finalAmount,
      movement_date: movementDate,
      due_date: dueDate || null,
      reason: activeTab === 'para_verildi' || activeTab === 'urun_verildi' ? reason : 'diger',
      description: description || null,
      product_name: prodName,
      model_code: mdlCode,
      quantity: qty,
      unit: unt,
      unit_price: up,
      currency_code: cc,
      exchange_rate: er,
      gold_rate: gr,
      tl_equivalent: cc !== 'TRY' ? tlEq : null,
      asset_name: assetName,
    });

    setSaving(false);
    if (res.error) { setErr(res.error); return; }
    onSuccess();
  };

  const tlEq = getTlEquivalent();
  const curLabel = AccountsDB.CURRENCY_LABELS[currencyCode];
  const dvzLabel = AccountsDB.CURRENCY_LABELS[currencyCodeDvz];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-background-50 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <h3 className="text-lg font-semibold text-foreground-900">Hesap Defterine Yaz</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500"></i>
          </button>
        </div>

        {/* Tab Selector — scrollable pills */}
        <div className="flex gap-1.5 px-5 pt-4 pb-2 overflow-x-auto scrollbar-none">
          {TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => { setActiveTab(tab.key); setErr(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-500 hover:text-foreground-700 bg-background-100'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 pt-3">
          {/* ============================================ */}
          {/* PARA VERİLDİ */}
          {/* ============================================ */}
          {(activeTab === 'para_verildi' || activeTab === 'para_alindi') && (
            <>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Para Birimi</label>
                <div className="flex gap-1">
                  {AccountsDB.CURRENCY_OPTIONS.map(cc => {
                    const cl = AccountsDB.CURRENCY_LABELS[cc];
                    return (
                      <button
                        key={cc}
                        type="button"
                        onClick={() => setCurrencyCode(cc)}
                        className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                          currencyCode === cc
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {cl.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Tutar * {currencyCode !== 'TRY' && <span className="text-foreground-400">({AccountsDB.CURRENCY_LABELS[currencyCode]?.symbol})</span>}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                  placeholder="0.00"
                />
              </div>

              {/* Exchange rate for non-TRY */}
              {currencyCode !== 'TRY' && (
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    {AccountsDB.CURRENCY_LABELS[currencyCode]?.label} Kuru ({currencyCode}/TRY) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      value={exchangeRate}
                      onChange={e => setExchangeRate(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                      placeholder="örn: 42.50"
                    />
                    {rates && (
                      <button
                        type="button"
                        onClick={() => {
                          if (currencyCode === 'USD') setExchangeRate(String(rates.usd_try));
                          else if (currencyCode === 'EUR') setExchangeRate(String(rates.eur_try));
                          else if (currencyCode === 'GBP' && rates.gbp_try) setExchangeRate(String(rates.gbp_try));
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] px-2 py-1 bg-background-100 text-foreground-500 rounded cursor-pointer hover:text-primary-600 whitespace-nowrap"
                      >
                        Güncel: {currencyCode === 'USD' ? rates.usd_try : currencyCode === 'EUR' ? rates.eur_try : rates.gbp_try || '—'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* TL Equivalent */}
              {currencyCode !== 'TRY' && tlEq > 0 && (
                <div className="p-3 rounded-lg bg-background-100/70 border border-background-200/50">
                  <p className="text-xs text-foreground-400">TL Karşılığı (deftere bu yazılır)</p>
                  <p className="text-lg font-bold text-foreground-800">₺{formatNumber(tlEq)}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih</label>
                  <input type="date" value={movementDate} onChange={e => setMovementDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Vade Tarihi</label>
                  <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
                </div>
              </div>

              {activeTab === 'para_verildi' && (
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Verilme Nedeni</label>
                  <select value={reason} onChange={e => setReason(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer">
                    {Object.entries(AccountsDB.VERILME_NEDENI_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* ============================================ */}
          {/* ÜRÜN VERİLDİ */}
          {/* ============================================ */}
          {activeTab === 'urun_verildi' && (
            <>
              <p className="text-xs text-foreground-400 italic bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                Stoktan düşmez, fatura oluşturmaz, satış sayılmaz. Sadece bu deftere not düşülür.
              </p>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün Adı *</label>
                <input
                  type="text"
                  value={productName}
                  onChange={e => setProductName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                  placeholder="Örn: Klima, Buzdolabı, Halı..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Model Kodu</label>
                <input
                  type="text"
                  value={modelCode}
                  onChange={e => setModelCode(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                  placeholder="Opsiyonel"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Adet</label>
                  <input type="number" step="1" value={quantity} onChange={e => setQuantity(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Birim Fiyat (TL)</label>
                  <input type="number" step="0.01" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Toplam Tutar</label>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 font-medium"
                    placeholder="0.00"
                  />
                  {!amount && Number(quantity) > 0 && Number(unitPrice) > 0 && (
                    <p className="text-[10px] text-foreground-400 mt-0.5">Hesaplanan: {(Number(quantity) * Number(unitPrice)).toLocaleString('tr-TR')} TL</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih</label>
                  <input type="date" value={movementDate} onChange={e => setMovementDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Vade Tarihi</label>
                  <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Verilme Nedeni</label>
                <select value={reason} onChange={e => setReason(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer">
                  {Object.entries(AccountsDB.VERILME_NEDENI_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* ============================================ */}
          {/* ALTIN VERİLDİ */}
          {/* ============================================ */}
          {activeTab === 'altin_verildi' && (
            <>
              <p className="text-xs text-foreground-400 italic bg-yellow-50 rounded-lg p-2.5 border border-yellow-100">
                Altın türünü seç, miktarı gir. Gram karşılığı otomatik hesaplanır (bilezik hariç).
              </p>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Altın Türü</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {AccountsDB.GOLD_TYPE_OPTIONS.map(gt => {
                    const info = AccountsDB.GOLD_TYPE_LABELS[gt];
                    return (
                      <button
                        key={gt}
                        type="button"
                        onClick={() => setGoldType(gt)}
                        className={`px-3 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                          goldType === gt
                            ? 'bg-amber-500 text-white'
                            : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {info.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    {goldType === 'bilezik' ? 'Gram Karşılığı' : 'Adet'}
                  </label>
                  <input
                    type="number"
                    step={goldType === 'bilezik' ? '0.01' : '1'}
                    value={goldType === 'bilezik' ? goldGramEquivalent : goldQuantity}
                    onChange={e => {
                      if (goldType === 'bilezik') setGoldGramEquivalent(e.target.value);
                      else setGoldQuantity(e.target.value);
                    }}
                    className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                    placeholder={goldType === 'bilezik' ? 'örn: 15.50' : 'örn: 2'}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    {goldType === 'bilezik' ? 'Gram Altın Kuru' : 'Gram Karşılığı'}
                  </label>
                  {goldType === 'bilezik' ? (
                    <input
                      type="number"
                      step="0.01"
                      value={goldRate}
                      onChange={e => setGoldRate(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                      placeholder="örn: 3200"
                    />
                  ) : (
                    <input
                      type="text"
                      value={goldGramEquivalent}
                      readOnly
                      className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-500"
                    />
                  )}
                </div>
              </div>

              {/* Gold rate for non-bilezik */}
              {goldType !== 'bilezik' && (
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Gram Altın Kuru (TL/gr) *</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={goldRate}
                      onChange={e => setGoldRate(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                      placeholder="örn: 3200.00"
                    />
                    {rates && rates.gold_try && (
                      <button
                        type="button"
                        onClick={() => setGoldRate(String(rates.gold_try))}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] px-2 py-1 bg-background-100 text-foreground-500 rounded cursor-pointer hover:text-primary-600 whitespace-nowrap"
                      >
                        Güncel: {rates.gold_try}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* TL Equivalent */}
              {tlEq > 0 && (
                <div className="p-3 rounded-lg bg-background-100/70 border border-background-200/50">
                  <p className="text-xs text-foreground-400">TL Karşılığı (deftere bu yazılır)</p>
                  <p className="text-lg font-bold text-foreground-800">₺{formatNumber(tlEq)}</p>
                  {goldGramEquivalent && (
                    <p className="text-xs text-foreground-400 mt-0.5">
                      {goldGramEquivalent} gr × {Number(goldRate).toLocaleString('tr-TR')} TL/gr
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih</label>
                <input type="date" value={movementDate} onChange={e => setMovementDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
              </div>
            </>
          )}

          {/* ============================================ */}
          {/* DÖVİZ VERİLDİ */}
          {/* ============================================ */}
          {activeTab === 'doviz_verildi' && (
            <>
              <p className="text-xs text-foreground-400 italic bg-sky-50 rounded-lg p-2.5 border border-sky-100">
                Elden döviz verme kaydı. Kur bilgi amaçlıdır, TL karşılığı deftere yazılır.
              </p>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Döviz Cinsi</label>
                <div className="flex gap-1">
                  {(['USD', 'EUR', 'GBP'] as const).map(cc => {
                    const cl = AccountsDB.CURRENCY_LABELS[cc];
                    return (
                      <button
                        key={cc}
                        type="button"
                        onClick={() => setCurrencyCodeDvz(cc)}
                        className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                          currencyCodeDvz === cc
                            ? 'bg-sky-500 text-white'
                            : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {cl.label} ({cl.symbol})
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Tutar ({currencyCodeDvz}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={currencyAmountDvz}
                    onChange={e => setCurrencyAmountDvz(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Kur (opsiyonel)</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      value={exchangeRateDvz}
                      onChange={e => setExchangeRateDvz(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                      placeholder="Bilgi amaçlı"
                    />
                    {rates && (
                      <button
                        type="button"
                        onClick={() => {
                          if (currencyCodeDvz === 'USD') setExchangeRateDvz(String(rates.usd_try));
                          else if (currencyCodeDvz === 'EUR') setExchangeRateDvz(String(rates.eur_try));
                          else if (currencyCodeDvz === 'GBP' && rates.gbp_try) setExchangeRateDvz(String(rates.gbp_try));
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] px-2 py-1 bg-background-100 text-foreground-500 rounded cursor-pointer hover:text-primary-600 whitespace-nowrap"
                      >
                        Güncel
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {tlEq > 0 && (
                <div className="p-3 rounded-lg bg-background-100/70 border border-background-200/50">
                  <p className="text-xs text-foreground-400">TL Karşılığı (deftere bu yazılır)</p>
                  <p className="text-lg font-bold text-foreground-800">₺{formatNumber(tlEq)}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih</label>
                <input type="date" value={movementDate} onChange={e => setMovementDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
              </div>
            </>
          )}

          {/* ============================================ */}
          {/* ÜRÜN İADE */}
          {/* ============================================ */}
          {activeTab === 'urun_iade' && (
            <>
              <p className="text-xs text-foreground-400 italic bg-rose-50 rounded-lg p-2.5 border border-rose-100">
                Daha önce verilen ürünün iade kaydı. Stok etkilemez, sadece bu deftere not düşülür.
              </p>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün Adı</label>
                <input
                  type="text"
                  value={returnProductName}
                  onChange={e => setReturnProductName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                  placeholder="İade edilen ürün"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">İade Miktar</label>
                  <input type="number" step="1" value={returnQuantity} onChange={e => setReturnQuantity(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">İade Tutarı (TL) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={returnAmount}
                    onChange={e => setReturnAmount(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih</label>
                <input type="date" value={movementDate} onChange={e => setMovementDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
              </div>
            </>
          )}

          {/* ============================================ */}
          {/* DÜZELTME */}
          {/* ============================================ */}
          {activeTab === 'duzeltme' && (
            <>
              <p className="text-xs text-foreground-400 italic bg-foreground-50 rounded-lg p-2.5 border border-foreground-100">
                Bakiye düzeltmesi. Artı: kişiye borç ekler, Eksi: kişiden tahsilat düşer.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCorrectionSign('positive')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap ${
                    correctionSign === 'positive'
                      ? 'bg-amber-100 text-amber-700 border border-amber-300'
                      : 'bg-background-100 text-foreground-500'
                  }`}
                >
                  <i className="ri-add-line mr-1"></i> Artı (+)
                </button>
                <button
                  type="button"
                  onClick={() => setCorrectionSign('negative')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap ${
                    correctionSign === 'negative'
                      ? 'bg-red-50 text-red-600 border border-red-200'
                      : 'bg-background-100 text-foreground-500'
                  }`}
                >
                  <i className="ri-subtract-line mr-1"></i> Eksi (-)
                </button>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tutar *</label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih</label>
                <input type="date" value={movementDate} onChange={e => setMovementDate(e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700" />
              </div>
            </>
          )}

          {/* Ortak: Açıklama */}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Açıklama (deftere not)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
              rows={2}
              placeholder="Deftere düşülecek not..."
              maxLength={500}
            />
          </div>

          {err && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer whitespace-nowrap">
              İptal
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-50">
              {saving ? 'Yazılıyor...' : 'Deftere Yaz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}